<template>
    <Select :modelValue="modelValue" :options="options" optionLabel="label" optionValue="value"
        :placeholder="placeholder || 'Select ' + labels.CLASS" :class="className" filter showClear
        @update:modelValue="onUpdate" @change="onChange" :loading="loading"
        data-testid="select-classroom-section" />
</template>

<script setup lang="ts">
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { getClassroomSectionLabel } from '@/utils/classroomLabel';
import type { Classroom } from '@/types/composition-functions';

const { labels } = useSchoolLabels();
const { schoolType } = useSchoolFeatures();

const props = defineProps({
  modelValue: {
    type: [String, Number, Object],
    default: null,
  },
  placeholder: {
    type: String,
    default: '',
  },
  showAll: {
    type: Boolean,
    default: false,
  },
  showClassroomsOnly: {
    type: Boolean,
    default: false,
  },
  // In showClassroomsOnly mode the option value is the classroom id. Enroll
  // flows submit a classroom_section_id instead: set this to emit the
  // classroom's first section pivot id (falls back to the classroom id when
  // the classroom has no sections).
  emitSectionId: {
    type: Boolean,
    default: false,
  },
  className: {
    type: String,
    default: 'w-full',
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { classrooms, loadingSchool: loading } = useSchoolQuery();

const options = computed(() => {
  const opts: Record<string, unknown>[] = [];

  if (props.showAll) {
    opts.push({ label: `All ${labels.value.CLASS}s`, value: 'ALL' });
  }

  if (classrooms.value) {
    if (props.showClassroomsOnly) {
      classrooms.value.forEach((c: Classroom) => {
        opts.push({
          label: c.name,
          value: props.emitSectionId ? (c.classroomSections?.[0]?.id ?? c.id) : c.id,
        });
      });
    } else {
      classrooms.value.forEach((c: Classroom) => {
        if (c?.classroomSections?.length) {
          c.classroomSections.forEach((cs) => {
            opts.push({
              label: getClassroomSectionLabel(cs, c, schoolType.value),
              value: cs?.id || '',
            });
          });
        }
      });
    }
  }

  return opts;
});

const onUpdate = (value: string | number | null) => {
  emit('update:modelValue', value);
};

const onChange = (event: { value: string | number | null }) => {
  emit('change', event);
};
</script>
